import { useQuery } from '@tanstack/react-query';
import { singletonHook } from 'react-singleton-hook';
import http from 'helpers/requests';
import queryClient from '../../../queryClient';
import Decimal from 'decimal.js';
import staticCountries from 'helpers/staticCountries.json';
import { useEmbedConfig, isPublicDiagramPath } from 'modules/Embed/useEmbedConfig';
import { isPublicCatalogPath } from 'modules/PublicCatalog/hooks/useFetchPublicCatalogShare';
// The i18next singleton, not `i18n/index.js` — only `.t` is used here, and the
// config module carries `import.meta.glob`, which Node's CJS loader cannot
// parse. See the note in helpers/requests.js: this file sits on the E2E
// require chain (fixtures/auth → matrix → permissionsMatrix → here).
import i18n from 'i18next';

// -------------------------------------------------------------------------------------
const initCnGoods = [];

const useUseCnGoodsImpl = () => {
  const { data } = useQuery(
    {
      queryKey: ['cngoods-cbam'],
      queryFn: async () => {
        const response = await http.get('/cngoods/cbam');
        return response?.data?.goods ?? [];
      },
      staleTime: 30 * 60 * 1000,
      gcTime: 60 * 60 * 1000,
    },
    queryClient,
  );
  return data ?? [];
};

export const useCnGoods = singletonHook(initCnGoods, useUseCnGoodsImpl);

const initCnGoodsMS = [];

const useUseCnGoodsMSImpl = () => {
  const cnGoods =
    useCnGoods()?.map((g) => ({
      label: [g.cnCode, g.name, g.aggregatedGoodsCategoryLabel ?? g.aggregatedGoodsCategory]
        .filter(Boolean)
        .join(' - '),
      value: g,
    })) ?? [];
  return cnGoods;
};

export const useCnGoodsMS = singletonHook(initCnGoodsMS, useUseCnGoodsMSImpl);

// -------------------------------------------------------------------------------------

export const useCbamCountries = () => {
  const { data } = useQuery(
    {
      queryKey: ['countries-cbam'],
      queryFn: async () => {
        const response = await http.get('/country/cbam');
        const countries = response?.data ?? [];
        if (countries.length === 0) throw new Error('Empty CBAM countries response');
        return countries;
      },
      staleTime: 24 * 60 * 60 * 1000,
      gcTime: 24 * 60 * 60 * 1000,
    },
    queryClient,
  );
  return data ?? [];
};

// -------------------------------------------------------------------------------------

export const useEUCountries = () => {
  const { data } = useQuery(
    {
      queryKey: ['countries-eu'],
      queryFn: async () => {
        const response = await http.get('/country/eu');
        const countries = response?.data ?? [];
        if (countries.length === 0) throw new Error('Empty EU countries response');
        return countries;
      },
      staleTime: 24 * 60 * 60 * 1000,
      gcTime: 24 * 60 * 60 * 1000,
    },
    queryClient,
  );
  return data ?? [];
};

// -------------------------------------------------------------------------------------

const fetchAllCountries = async () => {
  const response = await http.get('/country/all');
  const countries = response?.data ?? [];
  if (countries.length === 0) throw new Error('Empty all-countries response');
  return countries;
};

export const useAllCountries = () => {
  // Unauthenticated contexts (embed iframes, public diagram links, the public
  // catalog share page) can't call /country/all — serve the bundled snapshot
  // instead.
  const { isEmbed } = useEmbedConfig();
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const isPublic = isPublicDiagramPath(pathname) || isPublicCatalogPath(pathname);
  const skipAuth = isEmbed || isPublic;
  const { data } = useQuery(
    {
      queryKey: ['countries-all'],
      queryFn: fetchAllCountries,
      enabled: !skipAuth,
      staleTime: 24 * 60 * 60 * 1000,
      gcTime: 24 * 60 * 60 * 1000,
    },
    queryClient,
  );
  if (skipAuth) return staticCountries;
  return data ?? [];
};

// Awaitable variant for authenticated flows that must not proceed with an
// empty country list (e.g. emissions report generation). Joins any request
// useAllCountries already has in flight, or serves the fresh cache.
export const ensureAllCountries = () =>
  queryClient.fetchQuery({
    queryKey: ['countries-all'],
    queryFn: fetchAllCountries,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });

// -------------------------------------------------------------------------------------
const initAvailableCompanies = [];

const useAvailableCompaniesImpl = () => {
  const { data } = useQuery(
    {
      queryKey: ['declarant-companies'],
      queryFn: async () => {
        const response = await http.get('/declarant/companies/');
        return response?.data?.companies ?? [];
      },
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
    queryClient,
  );
  return data ?? [];
};

export const useAvailableCompanies = singletonHook(
  initAvailableCompanies,
  useAvailableCompaniesImpl,
);

export const CountryDropdown = Object.freeze({
  EU: 'eu',
  CBAM: 'cbam',
  ALL: 'all',
});

// The backend uses -1 as a magic value to denote "country is null". Country
// selects should treat it like an empty selection so the dropdown shows its
// "Please select a country" placeholder instead of rendering a literal -1.
export const NULL_COUNTRY = -1;

export const isNullCountry = (value) =>
  value == null || value === '' || Number(value) === NULL_COUNTRY;

export const FormControlTypes = Object.freeze({
  OODropdown: 'ooOperatorSelect',
  EUDropdown: 'euCountrySelect',
  CBAMDropdown: 'cbamCountrySelect',
  ALLDropdown: 'allCountrySelect',
  Dropdown: 'dropdown',
  DropdownSelect: 'dropdownSelect',
  Trigger: 'trigger',
  GoodsDropdown: 'cbamGoodsSelect',
  Checkbox: 'checkbox',
  Text: 'text',
  Numeric: 'numeric',
  Phone: 'phone',
  Hidden: 'hidden',
  Toggle: 'toggle',
});

export const OrganizationType = Object.freeze({
  Declarant: 'declarant',
  Operator: 'operator',
  Consultant: 'consultant',
  // A verification body (ADR-0006). Not a customer: no plan, no commerce surfaces, reads operator
  // data only through verification engagements.
  Verifier: 'verifier',
});

/** A Clerk org role that is the built-in Admin (`org:admin`, or `admin` from older payloads). */
export const isClerkAdminRole = (role) => role === 'org:admin' || role === 'admin';
// -------------------------------------------------------------------------------------

// `phase` uses ES getters (not a plain string) so the handful of consumers
// across CbamQuote/CostRadar/Procurement/CbamCosts/Analytics/Exports that
// destructure `{ label, value, phase }` straight off this module-scope
// array still see a localized value — a plain string would freeze at
// whatever language was active when the module first loaded.
export const cbamOptions = [
  {
    label: '2026',
    value: '97.5',
    get phase() {
      return i18n.t('common:cbamPhases.startOfDefinitivePeriod', 'Start of Definitive Period');
    },
  },
  { label: '2027', value: '95' },
  { label: '2028', value: '90' },
  { label: '2029', value: '77.5' },
  { label: '2030', value: '51.5' },
  { label: '2031', value: '39' },
  { label: '2032', value: '26.5' },
  { label: '2033', value: '14' },
  {
    label: '2034',
    value: '0',
    get phase() {
      return i18n.t('common:cbamPhases.fullImplementation', 'Full Implementation');
    },
  },
];
export const carbonPriceOptions = [
  { label: '2023', value: '83' },
  { label: '2024', value: '65' },
];
export const DirectOnlyCategories = ['Iron and Steel', 'Aluminium'];

/** UK CBAM charges direct emissions only in every sector; EU only in Iron & Steel and Aluminium. */
export const isDirectOnlyLine = (supercategory, isUk = false) =>
  isUk || DirectOnlyCategories.includes(supercategory);

// -------------------------------------------------------------------------------------
// SEFA (Specific Embedded Free Allocation) Utilities
// -------------------------------------------------------------------------------------

/**
 * Get CBAM factor for a given year, CLAMPED to the schedule exactly as the server does
 * (backend constants/cbamFactors.ts, unify-remaining-cost-calcs F0): a year before 2026
 * takes 97.5, a year after 2034 takes 0 — the regulation's full implementation, where it
 * used to answer 97.5 for any year outside the table.
 * @param {string|number} year - The reporting year
 * @returns {number} - CBAM factor as percentage (e.g., 97.5)
 */
export const getCbamFactorForYear = (year) => {
  const numeric = Number(year);
  const years = cbamOptions.map((opt) => Number(opt.label));
  const first = Math.min(...years);
  const last = Math.max(...years);
  if (!Number.isFinite(numeric) || numeric <= first) {
    return parseFloat(cbamOptions.find((opt) => Number(opt.label) === first).value);
  }
  if (numeric >= last) {
    return parseFloat(cbamOptions.find((opt) => Number(opt.label) === last).value);
  }
  const match = cbamOptions.find((opt) => Number(opt.label) === Math.trunc(numeric));
  return match ? parseFloat(match.value) : 0;
};

/**
 * Calculate SEFA from benchmark and CBAM factor
 * @param {number} benchmark - The benchmark value (tCO2e/tonne)
 * @param {number} cbamFactor - The CBAM factor percentage (e.g., 97.5)
 * @returns {number} - SEFA value
 */
export const calculateSefa = (benchmark, cbamFactor) => {
  if (!benchmark || !cbamFactor) return 0;
  return new Decimal(benchmark).mul(new Decimal(cbamFactor).div(100)).toNumber();
};

/**
 * Calculate benchmark from SEFA and CBAM factor (reverse calculation)
 * @param {number} sefa - The SEFA value
 * @param {number} cbamFactor - The CBAM factor percentage
 * @returns {number} - Benchmark value
 */
export const calculateBenchmarkFromSefa = (sefa, cbamFactor) => {
  if (!sefa || !cbamFactor || cbamFactor === 0) return 0;
  return new Decimal(sefa).div(new Decimal(cbamFactor).div(100)).toNumber();
};
